import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/common/Icon/Icon';
import type {
  CheckoutFormState,
  CheckoutPaymentResult,
  CheckoutPixPaymentInstructions,
  CheckoutSelectedPlan,
} from '../checkout.types';
import {
  CHECKOUT_PIX_POLL_INTERVAL_MS,
  CHECKOUT_PIX_REDIRECT_DELAY_MS,
  CHECKOUT_SUCCESS_DATA_QUERY_PARAM,
} from '../checkout.constants';
import { CheckoutPaymentStatusService } from '../../../services/CheckoutPaymentStatusService';
import { SharedTokenService } from '../../../services/SharedTokenService';
import { getCheckoutRoutePaths } from '../../../config/hostApps';
import { buildCheckoutSuccessTokenPayload } from '../checkoutSuccess.utils';
import CheckoutPaymentInstructionsPanel from './CheckoutPaymentInstructionsPanel';
import styles from './CheckoutPixWaitingView.module.css';

const CHECKOUT_SUCCESS_PATH = getCheckoutRoutePaths().success;

interface CheckoutPixWaitingViewProps {
  form: CheckoutFormState;
  selectedPlan: CheckoutSelectedPlan;
  checkoutResult: CheckoutPaymentResult;
  externalPaymentId: string;
  paymentInstructions: CheckoutPixPaymentInstructions;
  onRedirecting: () => void;
  isRedirecting?: boolean;
  mode?: 'checkout' | 'charge_checkout';
}

const CheckoutPixWaitingView: React.FC<CheckoutPixWaitingViewProps> = ({
  form,
  selectedPlan,
  checkoutResult,
  externalPaymentId,
  paymentInstructions,
  onRedirecting,
  isRedirecting: isRedirectingProp = false,
  mode = 'checkout',
}) => {
  const navigate = useNavigate();
  const [pollError, setPollError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(isRedirectingProp);
  const redirectStartedRef = useRef(false);
  const isChargeCheckout = mode === 'charge_checkout';

  const redirectAfterPaid = useCallback(async () => {
    if (redirectStartedRef.current) {
      return;
    }

    redirectStartedRef.current = true;
    setIsRedirecting(true);
    onRedirecting();

    try {
      const paidResult: CheckoutPaymentResult = {
        ...checkoutResult,
        message: isChargeCheckout
          ? 'Pagamento PIX confirmado. Obrigada!'
          : 'Pagamento PIX confirmado! Sua contratação foi concluída com sucesso.',
        paymentInstructions: null,
        externalPaymentId,
      };
      const tokenPayload = buildCheckoutSuccessTokenPayload(form, selectedPlan, paidResult, {
        mode,
      });
      const encoded = await SharedTokenService.encodeCheckoutSuccess(tokenPayload);

      window.setTimeout(() => {
        navigate(
          `${CHECKOUT_SUCCESS_PATH}?${CHECKOUT_SUCCESS_DATA_QUERY_PARAM}=${encodeURIComponent(encoded.token)}`,
          { replace: true },
        );
      }, CHECKOUT_PIX_REDIRECT_DELAY_MS);
    } catch (error) {
      redirectStartedRef.current = false;
      setIsRedirecting(false);
      setPollError(
        error instanceof Error
          ? error.message
          : 'Não foi possível finalizar a confirmação do pagamento.',
      );
    }
  }, [checkoutResult, externalPaymentId, form, isChargeCheckout, mode, navigate, onRedirecting, selectedPlan]);

  useEffect(() => {
    if (!externalPaymentId || isRedirecting) {
      return undefined;
    }

    let cancelled = false;

    const pollPaymentStatus = async () => {
      try {
        const paid = await CheckoutPaymentStatusService.isPaymentPaid(externalPaymentId);

        if (!cancelled && paid) {
          await redirectAfterPaid();
        }
      } catch (error) {
        if (!cancelled && !redirectStartedRef.current) {
          setPollError(
            error instanceof Error
              ? error.message
              : 'Não foi possível verificar o status do pagamento.',
          );
        }
      }
    };

    void pollPaymentStatus();
    const intervalId = window.setInterval(() => {
      void pollPaymentStatus();
    }, CHECKOUT_PIX_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [externalPaymentId, isRedirecting, redirectAfterPaid]);

  return (
    <article className={styles.waitingCard}>
      <div
        className={`${styles.waitingNotice} ${isRedirecting ? styles.waitingNoticeConfirmed : ''}`.trim()}
      >
        <header className={styles.waitingHeader}>
          <span className={styles.waitingIconWrap} aria-hidden>
            <Icon
              name={isRedirecting ? 'check_circle' : 'progress_activity'}
              size={36}
              filled={isRedirecting}
              color={isRedirecting ? 'var(--color-success)' : 'var(--color-warning)'}
              className={isRedirecting ? undefined : styles.spinner}
            />
          </span>
          <div className={styles.waitingHeaderText}>
            <h2 className={styles.waitingTitle}>
              {isRedirecting ? 'Pagamento confirmado!' : 'Aguardando pagamento PIX'}
            </h2>
            <p className={styles.waitingMessage}>
              {isRedirecting
                ? 'Redirecionando...'
                : 'Escaneie o QR Code ou use o código copia e cola abaixo. A confirmação aparece automaticamente após o pagamento.'}
            </p>
          </div>
        </header>
      </div>

      <CheckoutPaymentInstructionsPanel instructions={paymentInstructions} />

      {pollError && (
        <p className={styles.pollError} role="alert">
          {pollError}
        </p>
      )}
    </article>
  );
};

export default CheckoutPixWaitingView;
